import type { InsightField, TranscriptListItem, VttEntry } from "@/types/transcript";

export type TranscriptSortField =
	| "date"
	| "subject"
	| "organizer"
	| "participants"
	| "duration"
	| "linkedCandidates";

export type SortOrder = "asc" | "desc";

/** Tailwind color pairs cycled across speakers; all pairs are dark-mode safe. */
export const SPEAKER_COLORS = [
	{ bg: "bg-slate-100 dark:bg-slate-800/40", border: "border-slate-200 dark:border-slate-700", name: "text-slate-700 dark:text-slate-300" },
	{ bg: "bg-blue-50/70 dark:bg-blue-950/20", border: "border-blue-100 dark:border-blue-900", name: "text-blue-600 dark:text-blue-400" },
	{ bg: "bg-stone-100 dark:bg-stone-800/40", border: "border-stone-200 dark:border-stone-700", name: "text-stone-700 dark:text-stone-300" },
	{ bg: "bg-sky-50/70 dark:bg-sky-950/20", border: "border-sky-100 dark:border-sky-900", name: "text-sky-600 dark:text-sky-400" },
	{ bg: "bg-zinc-100 dark:bg-zinc-800/40", border: "border-zinc-200 dark:border-zinc-700", name: "text-zinc-700 dark:text-zinc-300" },
	{ bg: "bg-indigo-50/50 dark:bg-indigo-950/20", border: "border-indigo-100 dark:border-indigo-900", name: "text-indigo-600 dark:text-indigo-400" },
	{ bg: "bg-gray-100 dark:bg-gray-800/40", border: "border-gray-200 dark:border-gray-700", name: "text-gray-700 dark:text-gray-300" },
	{ bg: "bg-neutral-100 dark:bg-neutral-800/40", border: "border-neutral-200 dark:border-neutral-700", name: "text-neutral-600 dark:text-neutral-400" },
] as const;

type SpeakerColor = (typeof SPEAKER_COLORS)[number];

export function getSpeakerInitials(name: string): string {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2)
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	return name.substring(0, 2).toUpperCase();
}

export function parseVtt(vttContent: string): VttEntry[] {
	if (!vttContent)
		return [];

	// Normalize line endings (Microsoft often uses \r\n)
	const normalized = vttContent.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

	const entries: VttEntry[] = [];

	// Split on double newlines first
	let blocks = normalized.split(/\n\n+/);

	// If that only produces 1-2 blocks, the content might have single newlines only.
	// Fall back to splitting on each timestamp line.
	if (blocks.length <= 2) {
		const lines = normalized.split("\n");
		blocks = [];
		let currentBlock: string[] = [];

		for (const line of lines) {
			if (line.includes("-->")) {
				if (currentBlock.length > 0)
					blocks.push(currentBlock.join("\n"));
				currentBlock = [line];
			} else if (currentBlock.length > 0)
				currentBlock.push(line);
		}
		if (currentBlock.length > 0)
			blocks.push(currentBlock.join("\n"));
	}

	for (const block of blocks) {
		const lines = block.trim().split("\n");
		if (lines.length < 1)
			continue;

		const timestampLine = lines.find(l => l.includes("-->"));
		if (!timestampLine)
			continue;

		const timestamp = timestampLine.split("-->")[0].trim();
		const textLines = lines.filter(l => !l.includes("-->") && !/^\d+$/.test(l.trim()) && l.trim() !== "WEBVTT" && l.trim() !== "");

		let speaker = "";
		let text = textLines.join(" ");

		const speakerMatch = text.match(/<v\s+([^>]+)>/);
		if (speakerMatch) {
			speaker = speakerMatch[1];
			text = text.replace(/<v\s+[^>]+>/g, "").replace(/<\/v>/g, "").trim();
		}

		if (text)
			entries.push({ timestamp, speaker, text });
	}

	return entries;
}

export function formatDuration(startTime: string | null, endTime: string | null): string {
	if (!startTime || !endTime)
		return "";
	const start = new Date(startTime);
	const end = new Date(endTime);
	const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
	if (minutes < 60)
		return `${minutes}min`;
	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;
	return `${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}min` : ""}`;
}

export interface SpeakerMaps {
	colors: Map<string, SpeakerColor>;
	sides: Map<string, "left" | "right">;
}

/** Assign each speaker a stable color and chat side by order of appearance. */
export function buildSpeakerMaps(entries: VttEntry[]): SpeakerMaps {
	const colors = new Map<string, SpeakerColor>();
	const sides = new Map<string, "left" | "right">();
	let index = 0;
	for (const entry of entries) {
		if (entry.speaker && !colors.has(entry.speaker)) {
			colors.set(entry.speaker, SPEAKER_COLORS[index % SPEAKER_COLORS.length]);
			sides.set(entry.speaker, index % 2 === 0 ? "left" : "right");
			index++;
		}
	}
	return { colors, sides };
}

export function filterTranscripts(transcripts: TranscriptListItem[], search: string): TranscriptListItem[] {
	if (!search.trim())
		return transcripts;
	const query = search.toLowerCase();
	return transcripts.filter((tr) =>
		(tr.subject || "").toLowerCase().includes(query) ||
		(tr.organizer_email || "").toLowerCase().includes(query) ||
		tr.participants?.some((p) =>
			p.displayName?.toLowerCase().includes(query) || p.email?.toLowerCase().includes(query)
		) ||
		tr.linked_candidates?.some((c) => c.name?.toLowerCase().includes(query))
	);
}

function durationMs(transcript: TranscriptListItem): number {
	if (!transcript.start_time || !transcript.end_time)
		return 0;
	return new Date(transcript.end_time).getTime() - new Date(transcript.start_time).getTime();
}

export function sortTranscripts(
	transcripts: TranscriptListItem[],
	field: TranscriptSortField,
	order: SortOrder
): TranscriptListItem[] {
	const sorted = [...transcripts].sort((a, b) => {
		let cmp = 0;
		switch (field) {
			case "date":
				cmp = (a.start_time || "").localeCompare(b.start_time || "");
				break;
			case "subject":
				cmp = (a.subject || "").localeCompare(b.subject || "");
				break;
			case "organizer":
				cmp = (a.organizer_email || "").localeCompare(b.organizer_email || "");
				break;
			case "participants":
				cmp = (a.participants?.length || 0) - (b.participants?.length || 0);
				break;
			case "duration":
				cmp = durationMs(a) - durationMs(b);
				break;
			case "linkedCandidates":
				cmp = (a.linked_candidates?.length || 0) - (b.linked_candidates?.length || 0);
				break;
		}
		return order === "desc" ? -cmp : cmp;
	});
	return sorted;
}

export function filterVttEntries(entries: VttEntry[], search: string): VttEntry[] {
	if (!search.trim())
		return entries;
	const query = search.toLowerCase();
	return entries.filter((e) =>
		e.text.toLowerCase().includes(query) || e.speaker?.toLowerCase().includes(query)
	);
}

/**
 * Capturing regex for search highlighting. Consumers split on it and style
 * the odd-indexed parts — String.split puts capture-group matches at odd
 * positions, which avoids re-testing with a stateful global regex.
 */
export function buildHighlightRegex(search: string): RegExp | null {
	const query = search.trim();
	if (!query)
		return null;
	return new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
}

export function safeExtractValue(field: InsightField | undefined, defaultValue: InsightField["Extracted Value"] = null) {
	if (!field || typeof field !== "object")
		return defaultValue;
	return field["Extracted Value"] !== undefined ? field["Extracted Value"] : defaultValue;
}

export function safeExtractExplanation(field: InsightField | undefined): string {
	if (!field || typeof field !== "object")
		return "";
	return field.Explanation || "";
}

export type InsightLevel = "high" | "medium" | "low";

export function getInsightLevel(value: number): InsightLevel {
	if (value >= 0.7)
		return "high";
	if (value >= 0.4)
		return "medium";
	return "low";
}
